import { ReactElement } from 'react';

import {
  ChakraTab,
  ChakraTabList,
  ChakraTabs,
  SkeletonLoader,
  Text,
} from '@equip.health/ui';
import { Prompt } from 'react-router-dom';

import { PortalComponent } from '~/lib/contexts/ComponentPortalContext';
import useTabs from '../../../lib/hooks/useTabs';
import DiscardChangesModalV2 from '../DiscardChangesModalV2';

interface ProfileTabInfo {
  label: string;
  subPath: string;
}

interface ProfileTabsProps<T extends string | number | symbol> {
  basePath: string;
  componentPortalContextKey?: string;
  defaultPath: string;
  getTabIndex: (path: string) => number;
  Panels: () => ReactElement;
  portalComponent?: PortalComponent;
  tabsDictionary: Record<T, ProfileTabInfo>;
  isLoading?: boolean;
  title: string;
}

const ProfileTabs = <T extends string | number | symbol>({
  basePath,
  componentPortalContextKey,
  defaultPath,
  getTabIndex,
  Panels,
  portalComponent,
  tabsDictionary,
  title,
  isLoading,
}: ProfileTabsProps<T>) => {
  const PROFILE_TABS_ID = `${title.split(' ').join('-').toLowerCase()}__tabs`;
  const PROFILE_TAB_LIST_ID = `${PROFILE_TABS_ID}__tab-list`;

  const {
    cancelNavigation,
    confirmNavigation,
    hasUnsavedChanges,
    isVisible,
    onNavigate,
    onTabChange,
    onTabIndexChange,
    tabIndex,
  } = useTabs({
    basePath,
    componentPortalContextKey,
    defaultPath,
    getTabIndex,
    portalComponent,
  });

  return (
    <SkeletonLoader id={`${PROFILE_TABS_ID}__loader`} isLoaded={!isLoading}>
      <Prompt message={onNavigate} when={hasUnsavedChanges} />
      <DiscardChangesModalV2
        isOpen={isVisible}
        onClose={cancelNavigation}
        onConfirm={confirmNavigation}
      />
      <ChakraTabs
        id={PROFILE_TABS_ID}
        index={tabIndex}
        isLazy
        onChange={onTabIndexChange}
      >
        <ChakraTabList id={PROFILE_TAB_LIST_ID}>
          {Object.values(tabsDictionary).map(({ label, subPath }) => (
            <ChakraTab
              id={`${PROFILE_TAB_LIST_ID}__${subPath}`}
              key={subPath}
              marginRight="8px"
              onClick={() => {
                onTabChange(subPath);
                getTabIndex(subPath);
              }}
            >
              <Text
                id={`${PROFILE_TAB_LIST_ID}__${subPath}-label`}
                variant={
                  tabIndex === getTabIndex(subPath)
                    ? 'captionMedium'
                    : 'caption'
                }
              >
                {label}
              </Text>
            </ChakraTab>
          ))}
        </ChakraTabList>
        <Panels />
      </ChakraTabs>
    </SkeletonLoader>
  );
};

export default ProfileTabs;
